import { useEffect } from "react";

import browserHistory from "src/helpers/browser-history";
import { maybeRefetchNexus } from "src/helpers/nexus/refetch-controller";

/**
 * Wires the event-driven triggers that keep Nexus variants fresh mid-session so a
 * rollout/rollback takes effect without a full page reload. There is deliberately
 * NO interval polling — an idle tab issues zero requests. Every trigger is throttled
 * by the TTL dedupe floor inside maybeRefetchNexus (see refetch-controller.ts).
 *
 * Triggers:
 *  1. Tab becomes visible  — the rollout/rollback scenario: ops flips the variant,
 *     then the operator returns to the tab.
 *  2. In-app navigation    — refetch right before (re)entering a gated scene. The
 *     controller ignores refetches while unauthenticated, so this effectively only
 *     fires inside the authenticated app.
 */
export const useNexusLiveRefetch = (): void => {
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") maybeRefetchNexus();
    };
    document.addEventListener("visibilitychange", onVisible);

    const unlisten = browserHistory.listen(() => maybeRefetchNexus());

    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      unlisten();
    };
  }, []);
};
